import React from "react";
import ServiceDetailLayout from "../../components/service-detail-layout";
import gait from "../../assets/img/gait-analysis.jpg";

/**
 * Video Gait Analysis detail page component
 */
const VideoGaitAnalysisDetail = () => {
  return (
    <ServiceDetailLayout
      title="Video Gait Analysis"
      description="Professional video gait analysis with detailed recording of walking and running patterns. Includes printed analysis and advice for movement improvement."
      image={gait}
      imageAlt="Video Gait Analysis"
    >
      <h1 className="gs-u-mb+">Video Gait Analysis</h1>
      <p className="text--large gs-u-mb++">
        As you stroll or jog, it may seem like a simple activity, but your feet
        and legs are performing remarkably intricate functions to not just
        facilitate your movement but also to allow you to walk without
        discomfort, step by step, and day after day.
      </p>

      <p className="gs-u-mb++">
        A video analysis of your walking and running gait allows our podiatrists
        to examine the details of your gait closely, utilising this knowledge as
        a valuable resource to direct your care and treatment.
      </p>

      <h2 className="gs-u-mb+">
        What Is Video Gait Analysis and What Is It Used For?
      </h2>
      <p className="gs-u-mb++">
        Motion analysis of walking and running investigates the details of how
        your muscles, tendons, joints, and bones coordinate as you move,
        captured on video while you're exercising on our treadmill at the
        clinic. This is combined with a study of the pressure your feet and
        joints experience as they absorb and react to the daily stress placed on
        them. This thorough examination gives our podiatrists a lot of data to
        use in evaluating, identifying, and addressing any foot issues,
        preventing injuries, or enhancing your athletic performance.
      </p>

      <h2 className="gs-u-mb+">Video Gait Analysis Process</h2>
      <p className="gs-u-mb++">
        The process of video gait analysis begins by filming your natural
        walking or running style on a treadmill, setting the speed to match your
        usual pace. This video recording is then reviewed by both you and your
        foot specialist using our specialised software. Here, we examine your
        body's movement patterns, measure various aspects, and calculate angles
        to provide a detailed understanding of your foot's movements during
        walking. Our patients appreciate the slow playback of the video, as it
        allows us to clearly explain what we're observing and its implications
        for your foot health. This approach often results in moments of
        realisation for our patients, where the unexplained symptoms they've
        been experiencing suddenly make sense.
      </p>

      <h2 className="gs-u-mb+">In-shoe Pressure Analysis</h2>
      <p className="gs-u-mb++">
        Foot pressure mapping provides detailed insights into the strain exerted
        on various parts of your feet during standing or walking. We employ a
        particular method, installed within your footwear, that records detailed
        information on the distribution of pressure under your feet at every
        moment. This approach allows for a more comprehensive grasp of the
        forces acting on your feet and their relationship to any discomfort or
        issues you might be facing.
      </p>

      <h2 className="gs-u-mb+">What Video Gait Analysis Can Tell You</h2>
      <p className="gs-u-mb+">
        Video gait analysis as well as in-shoe pressure analysis can offer a
        range of insights including:
      </p>
      <ul className="gs-u-mb++ benefits-list">
        <li>Identifying atypical movement patterns</li>
        <li>Which biomechanical factors are contributing to your foot pain</li>
        <li>
          Why your injury may not be going away, or why you're not progressing
          in your recovery at the expected rate
        </li>
        <li>
          Where muscle imbalances may be present, including areas of tightness
          or weakness
        </li>
        <li>How your body is compensating for foot pain and problems</li>
        <li>
          Identifying features like leg length discrepancies - and the
          subsequent effects on your body
        </li>
        <li>
          Where improvements can be made to optimise your sports performance or
          general movement
        </li>
        <li>Ways to reduce your injury risk</li>
        <li>The pressure that your current shoes are exposing your feet to</li>
        <li>
          How your orthotics are altering the function of your feet and legs
        </li>
        <li>
          How your orthotics are changing the pressure experienced by the
          different areas of your feet with every step
        </li>
        <li>Guide optimal footwear selection</li>
      </ul>

      <h2 className="gs-u-mb+">
        How Does a Video Gait Analysis Assessment Work?
      </h2>
      <p className="gs-u-mb++">
        Your part in a video gait analysis is straightforward. Your podiatrist
        will assist you in placing the pressure analysis inserts into your
        footwear, and you'll be requested to walk as you normally would on a
        treadmill, if your pain or symptoms don't hinder or endanger you in any
        way. We'll activate the treadmill to a walking pace that feels natural
        to you, and you'll walk for about a minute, which includes roughly 30
        seconds of continuous recording. Depending on the type of injury you
        have and your involvement in sports, you might also be asked to run at a
        pace that feels natural to you on the treadmill.
      </p>

      <h2 className="gs-u-mb+">Benefits Of Video Gait Analysis</h2>
      <p className="gs-u-mb+">
        The advantages of using video gait analysis and in-shoe pressure
        assessment are rooted in the vast amount of data that this technology
        provides. This includes:
      </p>

      <h3 className="gs-u-mb+">Understand your physiology</h3>
      <p className="gs-u-mb++">
        The information and examination you get will provide you with a profound
        insight into your body's workings - the way your system is intricately
        coordinated to carry out physical activities. You'll gain a clearer
        understanding of the mechanisms and explanations for the functioning of
        your body, revealing the causes of any discomfort or issues you're
        facing.
      </p>

      <h3 className="gs-u-mb+">
        Diagnose conditions associated with running or sport
      </h3>
      <p className="gs-u-mb++">
        You get detailed, correct, and dependable information that helps shape
        your diagnosis and pinpoint the exact reasons for your foot issue,
        including those linked to running or sports. This allows you to gain a
        deeper understanding of the conditions and their origins, and then apply
        that understanding to develop the best treatment strategies.
      </p>

      <h3 className="gs-u-mb+">Improve foot, knee and back discomfort</h3>
      <p className="gs-u-mb++">
        Our feet serve as the base of our body, influencing the entire sequence
        from our ankles to our knees, hips, and spine. Whenever we experience
        pain, we can examine our body's movement patterns and the pressure on
        our feet to identify the source of the pain, and then make changes to
        enhance our mobility, leading to smooth, pain-free movement.
      </p>

      <h3 className="gs-u-mb+">Optimise your movement for running and sport</h3>
      <p className="gs-u-mb++">
        For certain of our clients, it's not primarily about addressing an
        injury or issue, but rather about assisting them in maintaining their
        peak performance. In such instances, we employ video gait analysis to
        pinpoint faulty movement habits, muscle imbalances, and other regions
        that could benefit from enhancements, ultimately aiming for the highest,
        most effective sports performance.
      </p>

      <h3 className="gs-u-mb+">
        Check the effects of treatments and interventions
      </h3>
      <p className="gs-u-mb++">
        Foot movement examination can also monitor your advancement and the
        impact of treatments like braces, allowing your podiatrist to swiftly
        make changes based on immediate responses as necessary.
      </p>
    </ServiceDetailLayout>
  );
};

export default VideoGaitAnalysisDetail;
